import React, { useState, useMemo, useEffect } from 'react'
import type { BuilderAngle, AvailabilityStatus } from '../types'
import { useBetBuilder } from '../context/BetContext'
import { useOdds } from '../context/OddsContext'
import { checkCorrelations, getCorrelationMessage } from '../utils/correlation'
import { angleMarketToOddsMarket } from '../utils'
import { calculateValueDisplay } from '../utils/odds'
import { AvailabilityBadge } from './AvailabilityBadge'
import { EdgeBadge } from './EdgeBadge'

interface AngleCardProps {
  angle: BuilderAngle
  fixtureId: number
  fixtureLabel: string
  homeTeamId?: number
  awayTeamId?: number
}

function getConfidenceStyle(confidence: string): React.CSSProperties {
  switch (confidence) {
    case 'high':
      return {
        background: 'rgba(74, 222, 128, 0.15)',
        borderColor: 'rgba(74, 222, 128, 0.4)',
        color: 'var(--green)',
      }
    case 'med':
      return {
        background: 'rgba(251, 191, 36, 0.15)',
        borderColor: 'rgba(251, 191, 36, 0.4)',
        color: 'var(--yellow)',
      }
    case 'low':
    default:
      return {
        background: 'rgba(251, 113, 133, 0.15)',
        borderColor: 'rgba(251, 113, 133, 0.4)',
        color: 'var(--red)',
      }
  }
}

function getLineChipStyle(
  line: number,
  selectedLine: number,
  confidence: string,
  isPrimary: boolean
): React.CSSProperties {
  const isSelected = line === selectedLine
  const confStyle = getConfidenceStyle(confidence)

  if (isSelected) {
    return {
      background: confStyle.background,
      borderColor: confStyle.borderColor,
      color: confStyle.color,
      cursor: 'pointer',
      fontWeight: 700,
    }
  }

  // Unselected - show muted style with subtle confidence hint
  return {
    background: 'rgba(255,255,255,.03)',
    borderColor: isPrimary ? confStyle.borderColor : 'var(--border)',
    color: 'var(--muted)',
    cursor: 'pointer',
    opacity: 0.7,
  }
}

// Derive market type from angle group/selection for live stat mapping
function getMarketType(angle: BuilderAngle): 'corners' | 'shots' | 'shots_on_target' | 'cards' | 'goals' | 'other' {
  const group = angle.group.toLowerCase()
  const label = angle.selection_label.toLowerCase()

  if (group.includes('corner') || label.includes('corner')) return 'corners'
  // Check for "shots on target" BEFORE general "shots" (more specific first)
  if (label.includes('shots on target') || label.includes('sot')) return 'shots_on_target'
  if (group.includes('shot') || label.includes('shot')) return 'shots'
  if (group.includes('card') || label.includes('card')) return 'cards'
  if (group.includes('goal') || label.includes('goal') || label.includes('btts') || label.includes('score')) return 'goals'
  return 'other'
}

// Derive team type for live stat lookup: 'home', 'away', or 'total'
function getTeamType(angle: BuilderAngle, homeTeamId?: number, awayTeamId?: number): 'home' | 'away' | 'total' {
  // Match-level bets (Total Match Corners, etc.) have scope="match" and no team_id
  if (angle.scope === 'match' || !angle.entity?.team_id) {
    return 'total'
  }

  // Team-level bets have team_id in entity
  const entityTeamId = angle.entity.team_id
  if (entityTeamId === homeTeamId) return 'home'
  if (entityTeamId === awayTeamId) return 'away'

  // Fallback: if we can't determine, use total
  return 'total'
}

export function AngleCard({ angle, fixtureId, fixtureLabel, homeTeamId, awayTeamId }: AngleCardProps) {
  // Use primary_line as default, fallback to first line
  const defaultLine = angle.primary_line ?? angle.suggested_lines[0] ?? 0.5
  const [selectedLine, setSelectedLine] = useState<number>(defaultLine)
  const [showTooltip, setShowTooltip] = useState(false)
  const [isOver, setIsOver] = useState(true)

  // Phase 6.5: Check player availability status
  const availabilityStatus = angle.entity?.availability_status as AvailabilityStatus | undefined
  const availabilityReason = angle.entity?.availability_reason as string | undefined
  const isUnavailable = availabilityStatus === 'injured' || availabilityStatus === 'suspended' || availabilityStatus === 'confirmed_out'

  const { addBet, removeBet, isBetSaved, bets } = useBetBuilder()
  const {
    fetchFixtureOddsData,
    getOddsForBookmaker,
    getBestOddsForSelection,
    preferredBookmaker,
    formatOdds,
    oddsHidden,
    showValueBadges,
    minEdgeThreshold,
  } = useOdds()

  // Fetch odds when component mounts or fixture changes
  useEffect(() => {
    if (fixtureId) {
      fetchFixtureOddsData(fixtureId)
    }
  }, [fixtureId, fetchFixtureOddsData])

  // Determine team type for market mapping
  const teamType = useMemo(() => {
    return getTeamType(angle, homeTeamId, awayTeamId)
  }, [angle, homeTeamId, awayTeamId])

  // Map angle market to odds market slug
  // For match_odds layouts, use market_type which has the correct slug format
  const oddsMarketSlug = useMemo(() => {
    const marketKey = angle.market_type || angle.market_id
    return angleMarketToOddsMarket(marketKey, teamType)
  }, [angle.market_type, angle.market_id, teamType])

  // Get odds for a specific line and outcome - returns both preferred and best
  const getLineOdds = (line: number, outcome: 'over' | 'under') => {
    if (!oddsMarketSlug) {
      return { preferred: null, best: null, bestBookmaker: null }
    }

    const preferredOdds = preferredBookmaker
      ? getOddsForBookmaker(fixtureId, oddsMarketSlug, outcome, preferredBookmaker.slug, line)
      : null
    const bestInfo = getBestOddsForSelection(fixtureId, oddsMarketSlug, outcome, line)

    return {
      preferred: preferredOdds,
      best: bestInfo?.odds ?? null,
      bestBookmaker: bestInfo?.bookmaker_slug ?? null,
    }
  }

  // Get odds for outcome-based markets (match_odds layout - no numeric lines)
  // Uses entity.outcome to lookup odds (e.g., 'home', 'draw', 'away', '1X', 'X2', '12')
  const getOutcomeOdds = () => {
    if (!oddsMarketSlug || !angle.entity?.outcome) {
      return { preferred: null, best: null, bestBookmaker: null }
    }

    const outcome = angle.entity.outcome as string
    const preferredOdds = preferredBookmaker
      ? getOddsForBookmaker(fixtureId, oddsMarketSlug, outcome, preferredBookmaker.slug, null)
      : null
    const bestInfo = getBestOddsForSelection(fixtureId, oddsMarketSlug, outcome, null)

    return {
      preferred: preferredOdds,
      best: bestInfo?.odds ?? null,
      bestBookmaker: bestInfo?.bookmaker_slug ?? null,
    }
  }

  // Check if this is a match_odds layout (outcome-based, not line-based)
  const isMatchOddsLayout = angle.layout_type === 'match_odds'

  // Helper to get probability for a specific line and direction (for edge calculation)
  const getLinePct = (line: number, isOverDirection: boolean): number => {
    const lineConv = angle.line_convictions?.[String(line)]
    const basePct = lineConv?.adjusted_pct ?? angle.conviction_pct ?? 0
    return isOverDirection ? basePct : 100 - basePct
  }

  // Check for correlations with existing bets
  const correlationWarnings = useMemo(() => {
    const marketType = getMarketType(angle)
    return checkCorrelations(
      marketType,
      angle.selection_label,
      bets.map(b => ({ id: b.id, title: b.title, marketType: b.marketType }))
    )
  }, [angle, bets])

  const correlationMessage = useMemo(
    () => getCorrelationMessage(correlationWarnings),
    [correlationWarnings]
  )

  // Show over/under toggle for all angles with suggested lines (team, player, match)
  // Exclude BTTS which is a yes/no bet, not over/under
  const isBtts = angle.market_id.includes('btts') || angle.selection_label.includes('BTTS')
  const showOverUnderToggle = angle.suggested_lines.length > 0 && !isBtts

  // Unique ID for this angle (include over/under)
  const overUnderSuffix = showOverUnderToggle ? (isOver ? '-over' : '-under') : ''
  const betId = `${angle.market_id}-${angle.selection_label}${overUnderSuffix}`
  const isAdded = isBetSaved(betId)

  // Get conviction data for currently selected line
  const selectedConviction = angle.line_convictions?.[String(selectedLine)]
  const baseRawPct = selectedConviction?.raw_pct ?? angle.conviction_pct ?? 0
  const baseAdjustedPct = selectedConviction?.adjusted_pct ?? angle.conviction_pct ?? 0

  // For unders, invert the probability
  const rawPct = isOver ? baseRawPct : 100 - baseRawPct
  const adjustedPct = isOver ? baseAdjustedPct : 100 - baseAdjustedPct

  // Recalculate confidence based on the displayed percentage
  const getConfidenceFromPct = (pct: number) => {
    if (pct >= 65) return 'high'
    if (pct >= 40) return 'med'
    return 'low'
  }
  const selectedConfidence = showOverUnderToggle
    ? getConfidenceFromPct(adjustedPct)
    : (selectedConviction?.confidence ?? angle.confidence)

  const confidenceStyle = getConfidenceStyle(selectedConfidence)

  const handleToggleBet = () => {
    if (isAdded) {
      removeBet(betId)
    } else {
      const betTitle = showOverUnderToggle
        ? `${angle.selection_label} ${isOver ? 'Over' : 'Under'}`
        : angle.selection_label
      addBet({
        id: betId,
        fixtureId,
        fixture: fixtureLabel,
        title: betTitle,
        line: selectedLine,
        marketType: getMarketType(angle),
        scope: angle.scope,
        teamType: getTeamType(angle, homeTeamId, awayTeamId),
        isOver,
        confidence: selectedConfidence,
        confidencePct: adjustedPct,
      })
    }
  }

  return (
    <div
      className={`card ${isUnavailable ? 'angle-unavailable' : ''}`}
      style={{
        background: 'rgba(255,255,255,.03)',
        opacity: isUnavailable ? 0.5 : 1,
      }}
    >
      <div className="card-inner angle">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="angle-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className={isUnavailable ? 'player-name-unavailable' : ''}>
              {angle.selection_label}
            </span>
            {angle.scope === 'player' && availabilityStatus && availabilityStatus !== 'available' && (
              <AvailabilityBadge status={availabilityStatus} reason={availabilityReason} compact />
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <span
              className="pill"
              style={{
                ...confidenceStyle,
                border: `1px solid ${confidenceStyle.borderColor}`,
                transition: 'all 0.2s ease',
                fontWeight: 700,
                minWidth: 42,
                textAlign: 'center',
                cursor: 'help',
              }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {adjustedPct}%
            </span>
            {showTooltip && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 8,
                  padding: '10px 12px',
                  background: '#1a2340',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  zIndex: 100,
                  minWidth: 200,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                  Probability Breakdown
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>Raw:</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{rawPct}%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>vs Opponent:</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: confidenceStyle.color }}>{adjustedPct}%</span>
                </div>

                {/* Factor breakdown for cards/fouls markets */}
                {(angle.support?.ref_factor != null && angle.support.ref_factor !== 1.0) ||
                 (angle.support?.ref_foul_factor != null && angle.support.ref_foul_factor !== 1.0) ||
                 (angle.support?.combined_card_factor != null && angle.support.combined_card_factor !== 1.0) ||
                 angle.support?.is_derby ||
                 angle.support?.is_relegation ? (
                  <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                      Factors Applied
                    </div>
                    {angle.support?.ref_factor != null && angle.support.ref_factor !== 1.0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Referee (Cards):</span>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: angle.support.ref_factor > 1 ? '#ff6b6b' : 'var(--green)'
                        }}>
                          {angle.support.ref_factor > 1 ? '+' : ''}{((angle.support.ref_factor - 1) * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                    {angle.support?.ref_foul_factor != null && angle.support.ref_foul_factor !== 1.0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Referee (Fouls):</span>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: angle.support.ref_foul_factor > 1 ? '#ff6b6b' : 'var(--green)'
                        }}>
                          {angle.support.ref_foul_factor > 1 ? '+' : ''}{((angle.support.ref_foul_factor - 1) * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                    {angle.support?.combined_card_factor != null && angle.support.combined_card_factor !== 1.0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Card Factor:</span>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: angle.support.combined_card_factor > 1 ? '#ff6b6b' : 'var(--green)'
                        }}>
                          {angle.support.combined_card_factor > 1 ? '+' : ''}{((angle.support.combined_card_factor - 1) * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                    {angle.support?.is_derby && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Derby:</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#ffa500' }}>+15%</span>
                      </div>
                    )}
                    {angle.support?.is_relegation && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Relegation:</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--red)' }}>+10%</span>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* xG breakdown for goals markets */}
                {(angle.support?.home_xg != null || angle.support?.away_xg != null ||
                  angle.support?.home_scoring != null || angle.support?.away_scoring != null) && (
                  <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                      Expected Goals (xG)
                    </div>
                    {angle.support?.home_scoring != null && angle.support?.away_scoring != null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Model xG:</span>
                        <span style={{ fontSize: 11, fontWeight: 600 }}>
                          {angle.support.home_scoring.toFixed(2)} + {angle.support.away_scoring.toFixed(2)} = {angle.support.total_avg?.toFixed(2) ?? (angle.support.home_scoring + angle.support.away_scoring).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {angle.support?.home_xg != null && angle.support?.away_xg != null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Season Avg xG:</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)' }}>
                          {angle.support.home_xg.toFixed(2)} vs {angle.support.away_xg.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {angle.support?.home_xg_against != null && angle.support?.away_xg_against != null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>xG Conceded:</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)' }}>
                          {angle.support.home_xg_against.toFixed(2)} vs {angle.support.away_xg_against.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {angle.support?.hist_total_avg != null && angle.support?.total_avg != null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>Historical Avg:</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)' }}>
                          {angle.support.hist_total_avg.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 8, lineHeight: 1.4 }}>
                  Raw = pure statistical probability<br />
                  Adjusted = accounts for opponent strength
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Match odds layout - outcome-based markets (1X2, Double Chance, Draw No Bet) */}
        {isMatchOddsLayout ? (
          <div className="match-odds-display">
            {!oddsHidden && (() => {
              const outcomeOdds = getOutcomeOdds()
              const hasOdds = outcomeOdds.preferred != null || outcomeOdds.best != null
              return hasOdds ? (
                <div className="outcome-odds-row">
                  <div className="outcome-odds-item">
                    <span className="odds-label">{preferredBookmaker?.name ?? 'Preferred'}</span>
                    <span className="odds-value preferred">
                      {outcomeOdds.preferred != null ? formatOdds(outcomeOdds.preferred) : '—'}
                      {showValueBadges && outcomeOdds.preferred != null && (() => {
                        const value = calculateValueDisplay(adjustedPct, outcomeOdds.preferred, minEdgeThreshold)
                        return value ? (
                          <EdgeBadge
                            value={value}
                            ourProbability={adjustedPct}
                            bookmakerName={preferredBookmaker?.name}
                          />
                        ) : null
                      })()}
                    </span>
                  </div>
                  <div className="outcome-odds-item">
                    <span className="odds-label">Best</span>
                    <span className="odds-value best">
                      {outcomeOdds.best != null ? formatOdds(outcomeOdds.best) : '—'}
                      {showValueBadges && outcomeOdds.best != null && (() => {
                        const value = calculateValueDisplay(adjustedPct, outcomeOdds.best, minEdgeThreshold)
                        return value ? (
                          <EdgeBadge
                            value={value}
                            ourProbability={adjustedPct}
                            bookmakerName={outcomeOdds.bestBookmaker ? `Best: ${outcomeOdds.bestBookmaker}` : 'Best odds'}
                          />
                        ) : null
                      })()}
                    </span>
                  </div>
                </div>
              ) : null
            })()}
          </div>
        ) : showOverUnderToggle ? (
          /* Two-column Over/Under layout */
          <div className="over-under-columns">
            {/* Over column */}
            <div
              className={`ou-column over ${isOver ? 'active' : ''}`}
              onClick={() => setIsOver(true)}
            >
              <div className="ou-column-header">
                <span>Over</span>
                {!oddsHidden && (
                  <div className="ou-odds-headers">
                    <span>{preferredBookmaker?.slug ?? 'Pref'}</span>
                    <span>Best</span>
                  </div>
                )}
              </div>
              <div className="ou-column-lines">
                {angle.suggested_lines.map((line) => {
                  const isPrimary = line === angle.primary_line
                  const isSelected = isOver && line === selectedLine
                  const lineOdds = getLineOdds(line, 'over')

                  return (
                    <div
                      key={line}
                      className={`ou-line-item ${isSelected ? 'selected' : ''} ${isPrimary ? 'primary' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsOver(true)
                        setSelectedLine(line)
                      }}
                    >
                      <span className="ou-line-value">{line}</span>
                      {!oddsHidden && (
                        <div className="ou-odds-values">
                          <span className="ou-line-odds preferred">
                            {lineOdds.preferred != null ? formatOdds(lineOdds.preferred) : '—'}
                            {showValueBadges && isSelected && lineOdds.preferred != null && (() => {
                              const value = calculateValueDisplay(getLinePct(line, true), lineOdds.preferred, minEdgeThreshold)
                              return value ? (
                                <EdgeBadge
                                  value={value}
                                  ourProbability={getLinePct(line, true)}
                                  bookmakerName={preferredBookmaker?.name}
                                />
                              ) : null
                            })()}
                          </span>
                          <span className="ou-line-odds best">
                            {lineOdds.best != null ? formatOdds(lineOdds.best) : '—'}
                            {showValueBadges && isSelected && lineOdds.best != null && (() => {
                              const value = calculateValueDisplay(getLinePct(line, true), lineOdds.best, minEdgeThreshold)
                              return value ? (
                                <EdgeBadge
                                  value={value}
                                  ourProbability={getLinePct(line, true)}
                                  bookmakerName={lineOdds.bestBookmaker ? `Best: ${lineOdds.bestBookmaker}` : 'Best odds'}
                                />
                              ) : null
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Under column */}
            <div
              className={`ou-column under ${!isOver ? 'active' : ''}`}
              onClick={() => setIsOver(false)}
            >
              <div className="ou-column-header">
                <span>Under</span>
                {!oddsHidden && (
                  <div className="ou-odds-headers">
                    <span>{preferredBookmaker?.slug ?? 'Pref'}</span>
                    <span>Best</span>
                  </div>
                )}
              </div>
              <div className="ou-column-lines">
                {angle.suggested_lines.map((line) => {
                  const isPrimary = line === angle.primary_line
                  const isSelected = !isOver && line === selectedLine
                  const lineOdds = getLineOdds(line, 'under')

                  return (
                    <div
                      key={line}
                      className={`ou-line-item ${isSelected ? 'selected' : ''} ${isPrimary ? 'primary' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsOver(false)
                        setSelectedLine(line)
                      }}
                    >
                      <span className="ou-line-value">{line}</span>
                      {!oddsHidden && (
                        <div className="ou-odds-values">
                          <span className="ou-line-odds preferred">
                            {lineOdds.preferred != null ? formatOdds(lineOdds.preferred) : '—'}
                            {showValueBadges && isSelected && lineOdds.preferred != null && (() => {
                              const value = calculateValueDisplay(getLinePct(line, false), lineOdds.preferred, minEdgeThreshold)
                              return value ? (
                                <EdgeBadge
                                  value={value}
                                  ourProbability={getLinePct(line, false)}
                                  bookmakerName={preferredBookmaker?.name}
                                />
                              ) : null
                            })()}
                          </span>
                          <span className="ou-line-odds best">
                            {lineOdds.best != null ? formatOdds(lineOdds.best) : '—'}
                            {showValueBadges && isSelected && lineOdds.best != null && (() => {
                              const value = calculateValueDisplay(getLinePct(line, false), lineOdds.best, minEdgeThreshold)
                              return value ? (
                                <EdgeBadge
                                  value={value}
                                  ourProbability={getLinePct(line, false)}
                                  bookmakerName={lineOdds.bestBookmaker ? `Best: ${lineOdds.bestBookmaker}` : 'Best odds'}
                                />
                              ) : null
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Non-over/under markets (like BTTS) - keep simple chips */
          <div className="chips">
            {angle.suggested_lines.map((line) => {
              const lineConviction = angle.line_convictions?.[String(line)]
              const lineConf = lineConviction?.confidence ?? angle.confidence
              const isPrimary = line === angle.primary_line
              const chipStyle = getLineChipStyle(line, selectedLine, lineConf, isPrimary)

              return (
                <span
                  key={line}
                  className="chip"
                  style={{
                    ...chipStyle,
                    transition: 'all 0.2s ease',
                    border: `1px solid ${chipStyle.borderColor}`,
                  }}
                  onClick={() => setSelectedLine(line)}
                >
                  {line}
                </span>
              )
            })}
          </div>
        )}
        {correlationMessage && !isAdded && (
          <div className="correlation-warning">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{correlationMessage}</span>
          </div>
        )}
        <div className="angle-bottom">
          <div className="small">{angle.why}</div>
          <button
            onClick={handleToggleBet}
            className={`add-btn ${isAdded ? 'added' : ''}`}
          >
            {isAdded ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
